/**
 * ContextLink — the canonical tappable reference to any domain object.
 *
 * CLAUDE.md mandate: "Every object reference is tappable (ContextLink).
 * NEVER show a plain text reference to another object."
 *
 * Usage:
 *   <ContextLink type="field" id={fieldId} label="Am Waldrand" />
 *   <ContextLink type="task" id={taskId} label="Düngung Feld 3" />
 *   <ContextLink type="note" id={noteId} label="Bodenprobe 2026" />
 *
 * Supported types: field, task, note, machine, worker, partner, activity, poi
 *
 * AppPolish Sprint 1.
 */

import { useTranslation } from 'react-i18next';

const TYPE_CONFIG = {
  field:    { icon: '🌾', route: (id) => `/fields/${id}`,    ariaKey: 'contextLink.field' },
  task:     { icon: '✅', route: (id) => `/tasks/${id}`,     ariaKey: 'contextLink.task' },
  note:     { icon: '📝', route: (id) => `/brain-dump/${id}`, ariaKey: 'contextLink.note' },
  machine:  { icon: '🚜', route: (id) => `/machines/${id}`,  ariaKey: 'contextLink.machine' },
  worker:   { icon: '👤', route: (id) => `/workers/${id}`,   ariaKey: 'contextLink.worker' },
  partner:  { icon: '🤝', route: (id) => `/partners/${id}`,  ariaKey: 'contextLink.partner' },
  activity: { icon: '📋', route: (id) => `/activities/${id}`, ariaKey: 'contextLink.activity' },
  poi:      { icon: '📍', route: (id) => `/pois/${id}`,      ariaKey: 'contextLink.poi' },
  // AI-extracted entity types (graph/brain-dump links)
  cow:      { icon: '🐄', route: (id) => `/herd/${id}`,      ariaKey: 'contextLink.cow' },
  pig:      { icon: '🐖', route: (id) => `/herd/${id}`,      ariaKey: 'contextLink.pig' },
  building: { icon: '🏠', route: (id) => `/buildings/${id}`,  ariaKey: 'contextLink.building' },
  supplier: { icon: '🏢', route: (id) => `/partners/${id}`,   ariaKey: 'contextLink.supplier' },
  crop:     { icon: '🌱', route: (id) => `/crops/${id}`,      ariaKey: 'contextLink.crop' },
  concept:  { icon: '💡', route: (id) => `/brain-dump/${id}`,  ariaKey: 'contextLink.concept' },
};

function navigateTo(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export default function ContextLink({ type, id, label, className = '', onClick }) {
  const { t } = useTranslation();
  const config = TYPE_CONFIG[type];

  if (!config || !id) {
    return <span className="text-[var(--aif-text-secondary)]">{label || '—'}</span>;
  }

  const ariaLabel = t(config.ariaKey, `Go to ${type}`) + ': ' + (label || id);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick({ type, id, label });
    } else {
      navigateTo(config.route(id));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      className={[
        'inline-flex items-center gap-[var(--aif-space-1)]',
        'min-h-[var(--aif-touch-min)] px-[var(--aif-space-2)] py-[var(--aif-space-1)]',
        'rounded-[var(--aif-radius-sm)]',
        'text-[var(--aif-text-sm)] font-[var(--aif-font-medium)]',
        'text-[var(--aif-color-primary)] hover:text-[var(--aif-color-primary-dark)]',
        'bg-transparent hover:bg-[var(--aif-bg-tertiary)]',
        'transition-colors duration-[var(--aif-transition)]',
        'cursor-pointer select-none',
        'focus-visible:outline-2 focus-visible:outline-[var(--aif-color-primary)] focus-visible:outline-offset-2',
        className,
      ].filter(Boolean).join(' ')}
      data-testid={`context-link-${type}-${id}`}
    >
      <span className="text-[var(--aif-text-base)] leading-none" aria-hidden="true">
        {config.icon}
      </span>
      <span className="truncate max-w-[200px]">
        {label || id}
      </span>
    </button>
  );
}

export { TYPE_CONFIG, navigateTo };
